// Auto-clicks the load-more button when the user scrolls near it.
//
// We use TWO mechanisms together because each one has a known weakness:
//   - scroll listener: reliable on iOS Safari (IntersectionObserver gets
//     throttled during momentum scroll), but only fires on actual scroll
//     events so a slow desktop wheel-scroll can land the button just inside
//     the trigger window without ever re-checking.
//   - IntersectionObserver: fires as soon as the button enters the prefetch
//     margin regardless of scroll velocity, and re-attaches cleanly when the
//     button is replaced (e.g. by facets.js re-rendering #ProductGridContainer
//     after a filter/sort change).
//
// We also re-query the button on every check rather than caching it, because
// facets.js swaps the entire product grid container on filter/sort, which
// detaches the original node along with any listeners pointing at it.

(() => {
  const PREFETCH_VIEWPORTS = 4;

  const getButton = () => document.querySelector('.js-btn-load-more');

  const isClickable = (button) => {
    if (!button || button.hasAttribute('disabled')) return false;
    const wrapper = button.closest('collection-load-more');
    if (wrapper && wrapper.classList.contains('hidden')) return false;
    return true;
  };

  let pending = false;
  const check = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      pending = false;
      const button = getButton();
      if (!isClickable(button)) return;

      const distanceBelowViewport =
        button.getBoundingClientRect().top - window.innerHeight;
      if (distanceBelowViewport < window.innerHeight * PREFETCH_VIEWPORTS) {
        button.click();
      }
    });
  };

  window.addEventListener('scroll', check, { passive: true });
  window.addEventListener('resize', check, { passive: true });

  let observedButton = null;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) check();
      }
    },
    { rootMargin: `${PREFETCH_VIEWPORTS * 100}% 0px` }
  );

  let mutationObserver = null;
  const watchButton = (button) => {
    if (button === observedButton) return;
    if (observedButton) observer.unobserve(observedButton);
    if (mutationObserver) mutationObserver.disconnect();
    observedButton = button;
    if (!button) return;

    observer.observe(button);
    mutationObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.attributeName === 'disabled' && !button.hasAttribute('disabled')) {
          check();
        }
      }
    });
    mutationObserver.observe(button, {
      attributes: true,
      attributeFilter: ['disabled'],
    });
  };

  // facets.js replaces #ProductGridContainer.innerHTML on filter/sort, which
  // swaps in a new load-more button. Watch the container so we can re-attach.
  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) {
    new MutationObserver(() => {
      watchButton(getButton());
      check();
    }).observe(gridContainer, { childList: true, subtree: true });
  }

  watchButton(getButton());
  check();
})();
